import {
  GetUserNotificationsCountDocument,
  useNotificationMarkAllAsReadMutation,
} from '@sketch/gql-types'
import { useEventDispatch } from '@sketch/utils'
import { useToast } from '@sketch/toasts'

import { notificationMarkAllAsReadOptimisticResponse } from '../../graphql'

// `userMarkedAllNotificationsAsRead` is augmented onto the events map in
// cloud-frontend (UpdatesOld). Re-declare the matching shape here so
// `@sketch/user`'s TS project sees it — declaration merging reconciles them.
declare module '@sketch/utils' {
  export interface EventsMap {
    userMarkedAllNotificationsAsRead: void
  }
}

/**
 * Marks every notification as read. Shared by the inbox header button and the
 * command-bar entry so their behavior (optimistic response, refetches, event
 * dispatch) can't drift apart.
 *
 * Returns the `useNotificationMarkAllAsReadMutation` tuple verbatim, so callers
 * get `loading` (true until the awaited refetches settle — see
 * `awaitRefetchQueries`) without tracking their own state.
 */
export const useMarkAllAsRead = () => {
  const { showToast } = useToast()
  const dispatchMarkedAllAsRead = useEventDispatch(
    'userMarkedAllNotificationsAsRead'
  )

  return useNotificationMarkAllAsReadMutation({
    redirectErrors: true,
    onError: 'do-nothing',
    optimisticResponse: {
      __typename: 'RootMutationType',
      ...notificationMarkAllAsReadOptimisticResponse,
    },
    // Refetch both the unread badge and the open notifications list — the new
    // inbox (unlike the legacy list) doesn't react to the event below, so the
    // cached entries need refetching to drop their unread dots.
    refetchQueries: [
      { query: GetUserNotificationsCountDocument },
      'getNotifications',
    ],
    awaitRefetchQueries: true,
    onCompleted: () => {
      dispatchMarkedAllAsRead()
      showToast('All notifications have been marked as read', 'positive')
    },
  })
}
